import { type FormEvent, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import type { ScheduledTaskDraft } from "~/api/client";
import { NotFoundError } from "~/api/errors";
import type { DigestTaskDraft, FlowTaskDraft, TriggerDraft } from "~/api/opsTypes";
import {
  useCreateDigestTask,
  useCreateFlowTask,
  useCreateScheduledTask,
  useDeleteScheduledTask,
  useFlows,
  useIdeSessions,
  useRouteIntent,
  useRunTaskNow,
  useScheduledTasks,
  useSetTaskPaused,
} from "~/api/queries";
import type { ScheduledTask } from "~/api/types";
import { Select, TextArea } from "~/features/settings/controls";
import { ALL_WEBHOOK_EVENTS } from "~/features/webhooks/taxonomy";
import { Button } from "~/foundation/components/Button";
import { ConfirmDialog } from "~/foundation/components/Dialog";
import { Empty } from "~/foundation/components/Empty";
import { Input } from "~/foundation/components/Input";
import { Spinner } from "~/foundation/components/Spinner";
import { Switch } from "~/foundation/components/Switch";
import { Text } from "~/foundation/components/Text";
import { formatRelativeTime } from "~/foundation/utils/time";

const UUID_RE = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i;
const MIN_INTERVAL = 5;

/** Pull a minute count out of loose recurrence encodings ("30m", "30", "PT30M"). */
/** The events an `on` trigger may wait for: the notification taxonomy, minus what a fire itself sends (BRIEF-CGNI S2-6). */
const ON_EVENTS = ALL_WEBHOOK_EVENTS.filter((id) => id !== "tasks.fired");

function parseMinutes(recurrence: string | null): number | null {
  if (!recurrence) {
    return null;
  }
  const value = recurrence.trim();
  if (/^\d+$/.test(value)) {
    return Number.parseInt(value, 10);
  }
  const isoDuration = value.match(/PT(\d+)M/i);
  if (isoDuration?.[1]) {
    return Number.parseInt(isoDuration[1], 10);
  }
  const shorthand = value.match(/(\d+)\s*m/i);
  if (shorthand?.[1]) {
    return Number.parseInt(shorthand[1], 10);
  }
  return null;
}

function looksLikeIso(value: string | null): value is string {
  return Boolean(value) && /\d{4}-\d{2}-\d{2}T/.test(value ?? "");
}

/** ISO instant → "2026-07-12 14:00" (UTC fields). */
function formatUtc(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) {
    return iso;
  }
  const isoStr = date.toISOString();
  return `${isoStr.slice(0, 10)} ${isoStr.slice(11, 16)}`;
}

/** ISO UTC instant → a value for `<input type="datetime-local">` in the browser's local time. */
function isoToLocalInput(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) {
    return "";
  }
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/** Table trigger column: humanised, tolerant of loose server encodings. */
function humanizeTrigger(
  task: ScheduledTask,
  t: (key: string, options?: Record<string, unknown>) => string,
): string {
  const kind = task.triggerKind.toLowerCase();
  if (kind === "on") {
    return task.onEvent
      ? t("When {{event}} happens", { event: task.onEvent })
      : t("When something happens");
  }
  if (kind === "every") {
    const mins = parseMinutes(task.recurrence);
    return mins ? t("Every {{count}} min", { count: mins }) : t("Recurring");
  }
  if (kind === "at") {
    const at = looksLikeIso(task.recurrence) ? task.recurrence : task.nextFireAt;
    return at ? t("At {{when}} UTC", { when: formatUtc(at) }) : t("At a specific time");
  }
  const fallbackMins = parseMinutes(task.recurrence);
  if (fallbackMins) {
    return t("Every {{count}} min", { count: fallbackMins });
  }
  if (looksLikeIso(task.recurrence)) {
    return t("At {{when}} UTC", { when: formatUtc(task.recurrence) });
  }
  return task.recurrence || task.triggerKind || t("Unknown");
}

function buildTrigger(
  triggerType: "at" | "every" | "on",
  atLocal: string,
  everyMinutes: number,
  t: (key: string) => string,
  onEvent = "",
): TriggerDraft | null {
  if (triggerType === "on") {
    if (!onEvent) {
      toast.error(t("Pick the event that runs it"));
      return null;
    }
    return { kind: "on", onEvent };
  }
  if (triggerType === "at") {
    if (!atLocal) {
      toast.error(t("Pick a date and time"));
      return null;
    }
    const date = new Date(atLocal);
    if (Number.isNaN(date.getTime())) {
      toast.error(t("Invalid date and time"));
      return null;
    }
    return { kind: "at", atUtc: date.toISOString() };
  }
  return { kind: "every", everyMinutes: Math.max(MIN_INTERVAL, Math.floor(everyMinutes || 0)) };
}

/**
 * Task Center: the scheduled-task table, a three-mode create form, and the fixed-bottom
 * prompt composer that prefills the form and creates nothing — propose → confirm → run.
 */
export function TaskCenter() {
  const { t, i18n } = useTranslation();
  const { data: tasks, isLoading, error } = useScheduledTasks();
  const { data: flowList } = useFlows();
  const { data: ideSessions } = useIdeSessions();
  const setPaused = useSetTaskPaused();
  const runNow = useRunTaskNow();
  const remove = useDeleteScheduledTask();
  const createIdeFlow = useCreateScheduledTask();
  const createDigest = useCreateDigestTask();
  const createFlow = useCreateFlowTask();
  const routeIntent = useRouteIntent();

  const flows = flowList?.flows ?? [];
  const isMissingEndpoint = error instanceof NotFoundError;

  const [deleting, setDeleting] = useState<ScheduledTask | null>(null);

  // Create-form state.
  const [taskType, setTaskType] = useState<"digest" | "flow" | "ideFlow">("digest");
  const [title, setTitle] = useState("");
  const [triggerType, setTriggerType] = useState<"at" | "every" | "on">("every");
  const [onEvent, setOnEvent] = useState("");
  const [atLocal, setAtLocal] = useState("");
  const [everyMinutes, setEveryMinutes] = useState(60);

  const [selectedFlowId, setSelectedFlowId] = useState("");
  const [freeFlowId, setFreeFlowId] = useState("");

  const [ideFlowSlug, setIdeFlowSlug] = useState("");
  const [idePrompt, setIdePrompt] = useState("");
  const [ideTargetSessionKey, setIdeTargetSessionKey] = useState("");
  const [ideTokenBudget, setIdeTokenBudget] = useState("");

  const [digestTopic, setDigestTopic] = useState("");
  const [digestSourceMode, setDigestSourceMode] = useState<"search" | "url" | "rss">("search");
  const [digestSources, setDigestSources] = useState("");
  const [digestThinkerId, setDigestThinkerId] = useState("lclgemma");
  const [digestTargetDoc, setDigestTargetDoc] = useState("");
  const [digestOutputMode, setDigestOutputMode] = useState<"prepend" | "replace">("prepend");
  const [digestNotify, setDigestNotify] = useState(true);

  const [promptText, setPromptText] = useState("");

  const resetForm = () => {
    setTitle("");
    setFreeFlowId("");
    setSelectedFlowId("");
    setAtLocal("");
    setEveryMinutes(60);
    setDigestTopic("");
    setDigestSources("");
    setDigestTargetDoc("");
    setIdeFlowSlug("");
    setIdePrompt("");
    setIdeTargetSessionKey("");
    setIdeTokenBudget("");
  };

  const submitCreate = (event: FormEvent) => {
    event.preventDefault();
    const trigger = buildTrigger(triggerType, atLocal, everyMinutes, t, onEvent);
    if (!trigger) {
      return;
    }

    if (taskType === "digest") {
      const sources = digestSources
        .split(/[\n,]/)
        .map((s) => s.trim())
        .filter(Boolean);
      if (!digestTopic.trim() && sources.length === 0) {
        toast.error(t("Enter an interest to search, or at least one source URL"));
        return;
      }
      const uuid = digestTargetDoc.match(UUID_RE)?.[0];
      if (!uuid) {
        toast.error(t("Enter the target document id (a UUID)"));
        return;
      }
      const draft: DigestTaskDraft = {
        ...(title.trim() ? { title: title.trim() } : {}),
        topic: digestTopic.trim(),
        sourceMode: digestSourceMode,
        sources,
        thinkerId: digestThinkerId.trim() || "lclgemma",
        targetDocumentId: uuid,
        outputMode: digestOutputMode,
        notifyInApp: digestNotify,
        trigger,
      };
      createDigest.mutate(draft, {
        onSuccess: () => {
          toast.success(t("Scheduled task created"));
          resetForm();
        },
      });
      return;
    }

    if (taskType === "ideFlow") {
      const slug = ideFlowSlug.trim();
      if (!slug) {
        toast.error(t("Choose the flow to run"));
        return;
      }
      const budget = Number.parseInt(ideTokenBudget, 10);
      const draft: ScheduledTaskDraft = {
        title: title.trim() || slug,
        flowSlug: slug,
        ...(idePrompt.trim() ? { prompt: idePrompt.trim() } : {}),
        ...(ideTargetSessionKey ? { targetSessionKey: ideTargetSessionKey } : {}),
        ...(Number.isFinite(budget) && budget > 0 ? { tokenBudget: budget } : {}),
        trigger,
      };
      createIdeFlow.mutate(draft, {
        onSuccess: () => {
          toast.success(t("Scheduled task created"));
          resetForm();
        },
      });
      return;
    }

    const flowId = (freeFlowId.trim() || selectedFlowId).trim();
    if (!flowId) {
      toast.error(t("Choose or enter a flow to schedule"));
      return;
    }
    const draft: FlowTaskDraft = { flowId, title: title.trim() || flowId, trigger };
    createFlow.mutate(draft, {
      onSuccess: () => {
        toast.success(t("Scheduled task created"));
        resetForm();
      },
    });
  };

  const creating = createDigest.isPending || createIdeFlow.isPending || createFlow.isPending;

  const submitPrompt = () => {
    const prompt = promptText.trim();
    if (!prompt) {
      return;
    }
    routeIntent.mutate(
      { prompt, surface: "tasks" },
      {
        onSuccess: (res) => {
          if (!res.actionId) {
            toast.error(res.message ?? t("Couldn't turn that into a task"));
            return;
          }
          // W7 turned `route` into a seven-member union on `actionId`. Task Center composes
          // ONE of those intents; everything else belongs to the Ask bar, which is where the
          // person is told so rather than silently getting an empty digest form.
          if (res.actionId !== "create-digest") {
            toast.message(t("That isn't a scheduled task — try the Ask bar (⌘⇧A)."));
            return;
          }
          const digest = res.digest;
          setTaskType("digest");
          setDigestTopic(digest?.topic ?? "");
          setDigestSourceMode(
            digest?.sourceMode === "url" || digest?.sourceMode === "rss"
              ? digest.sourceMode
              : "search",
          );
          setDigestSources(digest?.sources.join("\n") ?? "");
          setDigestThinkerId(digest?.thinkerId ?? "lclgemma");
          setDigestTargetDoc(digest?.targetDocumentId ?? "");
          setDigestOutputMode(digest?.outputMode === "replace" ? "replace" : "prepend");
          setDigestNotify(digest?.notifyInApp !== false);
          setTitle(res.title ?? "");
          const trigger = res.trigger;
          if (trigger?.kind === "at") {
            setTriggerType("at");
            setAtLocal(isoToLocalInput(trigger.atUtc));
          } else if (trigger?.kind === "on") {
            setTriggerType("on");
            setOnEvent(trigger.onEvent);
          } else {
            setTriggerType("every");
            setEveryMinutes(trigger?.kind === "every" ? trigger.everyMinutes : 1440);
          }

          if (res.missing.includes("targetDocumentId")) {
            toast.message(t("Pick the target document below (I couldn't match one), then Create."));
          } else if (digest?.targetDocumentTitle) {
            toast.success(
              t("Filled from your prompt → '{{doc}}'. Review and Create.", {
                doc: digest.targetDocumentTitle,
              }),
            );
          } else {
            toast.success(t("Filled from your prompt. Review and Create."));
          }
          setPromptText("");
        },
        onError: () => {
          toast.error(t("Couldn't understand that. Try naming the topic, sources, and how often."));
        },
      },
    );
  };

  const flowLabel = (task: ScheduledTask): string => {
    if (task.taskType.toLowerCase() === "ide-flow") {
      return task.flowId ? `${t("IDE flow")} · ${task.flowId}` : t("IDE flow");
    }
    if (!task.flowId) {
      return t("Digest");
    }
    const match = flows.find((flow) => flow.slug === task.flowId);
    return match?.name ?? task.flowId;
  };

  const busyRow = (id: string): boolean =>
    (setPaused.isPending && setPaused.variables?.id === id) ||
    (runNow.isPending && runNow.variables === id) ||
    (remove.isPending && remove.variables === id);

  return (
    <div className="flex flex-col gap-6 pb-28">
      <Text variant="small">
        {t(
          "Schedule a flow to run at a specific time or on a repeating interval. Schedules are durable — they survive restarts and fire server-side, without a browser open.",
        )}
      </Text>
      <div className="rounded-md border border-divider bg-bg-secondary px-3.5 py-2.5">
        <Text variant="small">
          {t(
            "Flows that contain ide:claude nodes only do real work while a Docit VS Code extension is connected. When none is connected, the scheduled run is still recorded but the ide:claude step is blocked until an editor is available.",
          )}
        </Text>
      </div>

      <div className="flex flex-col gap-2">
        <h3 className="text-body font-medium text-text">{t("Scheduled tasks")}</h3>
        {isLoading ? (
          <Spinner label={t("Loading")} />
        ) : isMissingEndpoint || !tasks?.length ? (
          <Empty message={t("No scheduled tasks yet — create one below.")} />
        ) : (
          <div className="overflow-x-auto rounded-md border border-divider">
            <table className="w-full text-start text-body">
              <thead>
                <tr className="border-b border-divider text-small text-text-tertiary">
                  <th className="px-3 py-2 text-start font-medium">{t("Title")}</th>
                  <th className="px-3 py-2 text-start font-medium">{t("Flow")}</th>
                  <th className="px-3 py-2 text-start font-medium">{t("Trigger")}</th>
                  <th className="px-3 py-2 text-start font-medium">{t("Next fire")}</th>
                  <th className="px-3 py-2 text-start font-medium">{t("Last fired")}</th>
                  <th className="px-3 py-2 text-start font-medium">{t("State")}</th>
                  <th className="px-3 py-2 text-start font-medium">{t("Actions")}</th>
                </tr>
              </thead>
              <tbody>
                {tasks.map((task) => {
                  const busy = busyRow(task.id);
                  return (
                    <tr key={task.id} className="border-b border-divider last:border-b-0">
                      <td className="px-3 py-2 text-text">{task.title || "—"}</td>
                      <td className="px-3 py-2 text-text-secondary">{flowLabel(task)}</td>
                      <td className="px-3 py-2 text-text-secondary">{humanizeTrigger(task, t)}</td>
                      <td className="whitespace-nowrap px-3 py-2 text-small text-text-tertiary">
                        {task.nextFireAt ? formatRelativeTime(task.nextFireAt, i18n.language) : "—"}
                      </td>
                      <td className="whitespace-nowrap px-3 py-2 text-small text-text-tertiary">
                        {task.lastFiredAt
                          ? formatRelativeTime(task.lastFiredAt, i18n.language)
                          : t("Never")}
                      </td>
                      <td className="px-3 py-2 text-text-secondary">
                        {task.paused ? t("paused") : t("active")}
                      </td>
                      <td className="px-3 py-2">
                        <div className="flex flex-wrap gap-1.5">
                          <Button
                            size="sm"
                            disabled={busy || task.abilities.manage !== true}
                            onClick={() => setPaused.mutate({ id: task.id, paused: !task.paused })}
                          >
                            {task.paused ? t("Resume") : t("Pause")}
                          </Button>
                          <Button
                            size="sm"
                            disabled={busy || task.abilities.manage !== true}
                            onClick={() =>
                              runNow.mutate(task.id, {
                                onSuccess: (result) => {
                                  toast.success(t("Run started"));
                                  if (result.note) {
                                    toast.message(result.note);
                                  }
                                },
                              })
                            }
                          >
                            {t("Run now")}
                          </Button>
                          <Button
                            size="sm"
                            variant="danger"
                            disabled={busy || task.abilities.manage !== true}
                            onClick={() => setDeleting(task)}
                          >
                            {t("Delete")}
                          </Button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <form onSubmit={submitCreate} className="flex max-w-2xl flex-col gap-4">
        <h3 className="text-body font-medium text-text">{t("Schedule a task")}</h3>

        <fieldset className="flex flex-col gap-1.5">
          <legend className="text-small font-medium text-text-secondary">{t("Task type")}</legend>
          <div className="flex flex-wrap gap-4">
            {(
              [
                ["digest", "News / topic digest"],
                ["flow", "Run a flow"],
                ["ideFlow", "IDE flow (runs on a connected editor)"],
              ] as const
            ).map(([value, label]) => (
              <label
                key={value}
                className="inline-flex cursor-pointer items-center gap-1.5 text-body text-text"
              >
                <input
                  type="radio"
                  name="taskType"
                  checked={taskType === value}
                  onChange={() => setTaskType(value)}
                />
                {t(label)}
              </label>
            ))}
          </div>
        </fieldset>

        {taskType === "digest" ? (
          <>
            <Input
              label={t("Interest / topic")}
              value={digestTopic}
              onChange={(event) => setDigestTopic(event.target.value)}
              placeholder={t("e.g., AI regulation in the EU")}
              fullWidth
            />
            <div className="flex flex-col gap-1.5">
              <Select
                label={t("Source")}
                value={digestSourceMode}
                onChange={(event) =>
                  setDigestSourceMode(event.target.value as "search" | "url" | "rss")
                }
                fullWidth
              >
                <option value="search">{t("Web search on the topic")}</option>
                <option value="url">{t("Specific page URL(s)")}</option>
                <option value="rss">{t("RSS / feed URL(s)")}</option>
              </Select>
              {digestSourceMode !== "search" ? (
                <TextArea
                  value={digestSources}
                  onChange={(event) => setDigestSources(event.target.value)}
                  placeholder={t("One URL per line")}
                  rows={3}
                  aria-label={t("Sources")}
                />
              ) : null}
              <Text variant="small">
                {digestSourceMode === "search"
                  ? t("Each run searches the web for your topic and digests the top results.")
                  : t("Each run fetches these URLs and digests them.")}
              </Text>
            </div>
            <Input
              label={t("Model / thinker")}
              value={digestThinkerId}
              onChange={(event) => setDigestThinkerId(event.target.value)}
              placeholder="lclgemma"
              fullWidth
            />
            <Input
              label={t("Target document id")}
              value={digestTargetDoc}
              onChange={(event) => setDigestTargetDoc(event.target.value)}
              placeholder={t("Paste the document's id (UUID)")}
              fullWidth
            />
            <Select
              label={t("Update mode")}
              value={digestOutputMode}
              onChange={(event) => setDigestOutputMode(event.target.value as "prepend" | "replace")}
              fullWidth
            >
              <option value="prepend">{t("Add newest on top (keep history)")}</option>
              <option value="replace">{t("Replace with the latest only")}</option>
            </Select>
            <div className="flex flex-col gap-1">
              <Switch
                checked={digestNotify}
                onCheckedChange={setDigestNotify}
                label={t("Notify me in-app when a run updates the document")}
                description={t(
                  "You'll get a notification that deep-links to the refreshed document each time this runs.",
                )}
              />
            </div>
          </>
        ) : taskType === "ideFlow" ? (
          <>
            <div className="flex flex-col gap-1.5">
              <Select
                label={t("Flow")}
                value={ideFlowSlug}
                onChange={(event) => setIdeFlowSlug(event.target.value)}
                fullWidth
              >
                <option value="">{t("Select a flow…")}</option>
                {flows.map((flow) => (
                  <option key={flow.slug} value={flow.slug}>
                    {flow.name || flow.slug}
                    {flow.status ? ` (${flow.status})` : ""}
                  </option>
                ))}
              </Select>
              <Text variant="small">
                {t(
                  "An ai-flow from Flows. Each fire queues a dispatch that a connected IDE window claims and executes; with no live window the dispatch waits until its TTL.",
                )}
              </Text>
            </div>
            <TextArea
              label={t("Prompt")}
              value={idePrompt}
              onChange={(event) => setIdePrompt(event.target.value)}
              placeholder={t("Optional — the run prompt; blank uses the flow's default prompt")}
              rows={3}
            />
            <div className="flex flex-col gap-1.5">
              <Select
                label={t("Target window")}
                value={ideTargetSessionKey}
                onChange={(event) => setIdeTargetSessionKey(event.target.value)}
                fullWidth
              >
                <option value="">{t("Any online window")}</option>
                {(ideSessions ?? [])
                  .filter((session) => session.isOnline)
                  .map((session) => (
                    <option key={session.sessionKey} value={session.sessionKey}>
                      {session.machineName ?? session.sessionKey}
                      {session.workspaceName ? ` · ${session.workspaceName}` : ""}
                    </option>
                  ))}
              </Select>
              <Text variant="small">
                {t(
                  "The window is checked at fire time, not now — a named window that is offline when the trigger fires leaves the dispatch queued.",
                )}
              </Text>
            </div>
            <Input
              type="number"
              label={t("Token budget")}
              value={ideTokenBudget}
              onChange={(event) => setIdeTokenBudget(event.target.value)}
              placeholder={t("Optional — ceiling for each run")}
              fullWidth
            />
          </>
        ) : (
          <div className="flex flex-col gap-1.5">
            <span className="text-small font-medium text-text-secondary">{t("Flow")}</span>
            {flows.length > 0 ? (
              <Select
                aria-label={t("Flow")}
                value={selectedFlowId}
                onChange={(event) => setSelectedFlowId(event.target.value)}
                fullWidth
              >
                <option value="">{t("Select a flow…")}</option>
                {flows.map((flow) => (
                  <option key={flow.slug} value={flow.slug}>
                    {flow.name || flow.slug}
                    {flow.status ? ` (${flow.status})` : ""}
                  </option>
                ))}
              </Select>
            ) : null}
            <Input
              aria-label={t("Flow id")}
              value={freeFlowId}
              onChange={(event) => setFreeFlowId(event.target.value)}
              placeholder={t("…or type a flow id, e.g. documentation-syncing")}
              fullWidth
            />
            <Text variant="small">
              {t(
                "Pick a flow from the list, or type a flow id directly to schedule one that isn't listed.",
              )}
            </Text>
          </div>
        )}

        <Input
          label={t("Title")}
          value={title}
          onChange={(event) => setTitle(event.target.value)}
          placeholder={t("Optional — a name for this task")}
          fullWidth
        />

        <fieldset className="flex flex-col gap-2">
          <legend className="text-small font-medium text-text-secondary">{t("Trigger")}</legend>
          <div className="flex flex-wrap gap-4">
            <label className="inline-flex cursor-pointer items-center gap-1.5 text-body text-text">
              <input
                type="radio"
                name="triggerType"
                checked={triggerType === "at"}
                onChange={() => setTriggerType("at")}
              />
              {t("At a specific time")}
            </label>
            <label className="inline-flex cursor-pointer items-center gap-1.5 text-body text-text">
              <input
                type="radio"
                name="triggerType"
                checked={triggerType === "every"}
                onChange={() => setTriggerType("every")}
              />
              {t("Every interval")}
            </label>
            <label className="inline-flex cursor-pointer items-center gap-1.5 text-body text-text">
              <input
                type="radio"
                name="triggerType"
                checked={triggerType === "on"}
                onChange={() => setTriggerType("on")}
              />
              {t("When something happens")}
            </label>
          </div>

          {triggerType === "on" ? (
            <div className="flex flex-col gap-1">
              <select
                className="max-w-80 rounded border border-divider bg-bg px-2 py-1 text-body text-text"
                value={onEvent}
                onChange={(event) => setOnEvent(event.target.value)}
                aria-label={t("Event")}
                dir="ltr"
              >
                <option value="">{t("Pick an event")}</option>
                {ON_EVENTS.map((id) => (
                  <option key={id} value={id}>
                    {id}
                  </option>
                ))}
              </select>
              <Text variant="small">
                {t(
                  "It runs each time this workspace sends the event; a burst within a minute runs it once.",
                )}
              </Text>
            </div>
          ) : triggerType === "at" ? (
            <div className="flex flex-col gap-1">
              <Input
                type="datetime-local"
                value={atLocal}
                onChange={(event) => setAtLocal(event.target.value)}
                aria-label={t("Run at")}
                className="max-w-56"
              />
              <Text variant="small">
                {t("Uses your local time zone; it is converted to UTC before scheduling.")}
              </Text>
            </div>
          ) : (
            <div className="flex flex-col gap-1">
              <div className="flex items-center gap-2">
                <Input
                  type="number"
                  min={MIN_INTERVAL}
                  step={5}
                  value={everyMinutes}
                  onChange={(event) =>
                    setEveryMinutes(Number.parseInt(event.target.value, 10) || 0)
                  }
                  aria-label={t("Interval in minutes")}
                  className="max-w-28"
                />
                <Text variant="small">{t("minutes")}</Text>
              </div>
              <Text variant="small">{t("Minimum interval is 5 minutes.")}</Text>
            </div>
          )}
        </fieldset>

        <div>
          <Button type="submit" variant="primary" disabled={creating}>
            {t("Create scheduled task")}
          </Button>
        </div>
      </form>

      {/* Fixed-bottom prompt composer: propose → confirm → create, never propose → create. */}
      <div className="fixed inset-x-0 bottom-4 z-20 mx-auto flex w-[min(760px,calc(100vw-2rem))] items-center gap-2 rounded-md border border-divider bg-bg-secondary p-2.5 shadow-menu">
        <span aria-hidden className="text-body">
          ✨
        </span>
        <input
          value={promptText}
          onChange={(event) => setPromptText(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === "Enter" && !event.shiftKey) {
              event.preventDefault();
              submitPrompt();
            }
          }}
          placeholder={t(
            'Describe a task — e.g. "digest AI news from Hacker News and TechCrunch every morning into my Rolling Digest doc and notify me"',
          )}
          disabled={routeIntent.isPending}
          aria-label={t("Describe a task in words")}
          className="h-8 min-w-0 flex-1 rounded-sm border border-input-border bg-bg px-2.5 text-small text-text focus:border-input-border-focus focus:outline-none"
        />
        <Button
          size="sm"
          variant="primary"
          onClick={submitPrompt}
          disabled={routeIntent.isPending || !promptText.trim()}
        >
          {routeIntent.isPending ? <Spinner size={14} /> : t("Compose")}
        </Button>
      </div>

      {deleting ? (
        <ConfirmDialog
          open
          onOpenChange={(open) => !open && setDeleting(null)}
          title={t("Delete scheduled task")}
          description={t("Delete this scheduled task? This cannot be undone.")}
          confirmLabel={t("Delete")}
          cancelLabel={t("Cancel")}
          closeLabel={t("Close")}
          dangerous
          busy={remove.isPending}
          onConfirm={() =>
            remove.mutate(deleting.id, {
              onSuccess: () => {
                setDeleting(null);
                toast.success(t("Task deleted"));
              },
            })
          }
        />
      ) : null}
    </div>
  );
}
